import {
  httpsCallable,
} from "firebase/functions";
import {
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { functions } from "../../firebase";
import {
  TV_DEVICE_ONLINE_THRESHOLD_SECONDS,
  TV_DEVICE_STATUSES,
  TV_PAIRING_STATUSES,
} from "./tvDisplayConstants";
import {
  getTvDisplayDevicesCollectionRef,
  getTvDisplayPairingsCollectionRef,
} from "./tvDisplayService";

const callApproveTvDisplayPairing = httpsCallable(functions, "approveTvDisplayPairing");
const callRejectTvDisplayPairing = httpsCallable(functions, "rejectTvDisplayPairing");
const callRenameTvDisplayDevice = httpsCallable(functions, "renameTvDisplayDevice");
const callUpdateTvDisplayDeviceDetails = httpsCallable(functions, "updateTvDisplayDeviceDetails");
const callEnableTvDisplayDevice = httpsCallable(functions, "enableTvDisplayDevice");
const callDisableTvDisplayDevice = httpsCallable(functions, "disableTvDisplayDevice");
const callRevokeTvDisplayDevice = httpsCallable(functions, "revokeTvDisplayDevice");
const callDeleteTvDisplayDevice = httpsCallable(functions, "deleteTvDisplayDevice");

export function subscribeToTvDevices(callback, errorCallback) {
  const q = query(getTvDisplayDevicesCollectionRef(), orderBy("registeredAt", "desc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizeTvDevice(entry));
      if (typeof callback === "function") callback(rows);
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export function subscribeToTvPairings(callback, errorCallback) {
  const q = query(getTvDisplayPairingsCollectionRef(), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((entry) => normalizeTvPairing(entry));
      if (typeof callback === "function") callback(rows);
    },
    (error) => {
      if (typeof errorCallback === "function") errorCallback(error);
    }
  );
}

export async function approveTvPairing(data = {}) {
  const result = await callApproveTvDisplayPairing(data);
  return result.data || {};
}

export async function rejectTvPairing(data = {}) {
  const result = await callRejectTvDisplayPairing(data);
  return result.data || {};
}

export async function renameTvDevice(deviceId, deviceName) {
  const result = await callRenameTvDisplayDevice({ deviceId, deviceName });
  return result.data || {};
}

export async function updateTvDeviceDetails(data = {}) {
  const result = await callUpdateTvDisplayDeviceDetails(data);
  return result.data || {};
}

export async function enableTvDevice(deviceId) {
  const result = await callEnableTvDisplayDevice({ deviceId });
  return result.data || {};
}

export async function disableTvDevice(deviceId) {
  const result = await callDisableTvDisplayDevice({ deviceId });
  return result.data || {};
}

export async function revokeTvDevice(deviceId) {
  const result = await callRevokeTvDisplayDevice({ deviceId });
  return result.data || {};
}

export async function deleteTvDevice(deviceId) {
  const result = await callDeleteTvDisplayDevice({ deviceId });
  return result.data || {};
}

export function getComputedTvDeviceStatus(device = {}) {
  if (device.enabled === false) {
    return device.pairingStatus === TV_PAIRING_STATUSES.REVOKED ?
      TV_DEVICE_STATUSES.REVOKED :
      TV_DEVICE_STATUSES.DISABLED;
  }

  const lastSeenAtMs = toMillis(device.lastSeenAt);
  if (!lastSeenAtMs) return TV_DEVICE_STATUSES.OFFLINE;

  const thresholdMs = TV_DEVICE_ONLINE_THRESHOLD_SECONDS * 1000;
  return Date.now() - lastSeenAtMs <= thresholdMs ?
    TV_DEVICE_STATUSES.ONLINE :
    TV_DEVICE_STATUSES.OFFLINE;
}

function normalizeTvDevice(snapshot) {
  const data = snapshot.data() || {};
  const location = data.location || {};
  const deviceInfo = data.deviceInfo || {};
  const normalized = {
    id: snapshot.id,
    deviceId: String(data.deviceId || snapshot.id || ""),
    authUid: String(data.authUid || ""),
    deviceName: String(data.deviceName || ""),
    enabled: data.enabled !== false,
    pairingStatus: String(data.pairingStatus || TV_PAIRING_STATUSES.PAIRED),
    status: String(data.status || TV_DEVICE_STATUSES.OFFLINE),
    city: String(location.city || data.city || ""),
    state: String(location.state || data.state || ""),
    country: String(location.country || data.country || ""),
    notes: String(data.notes || ""),
    manufacturer: String(deviceInfo.manufacturer || data.manufacturer || ""),
    model: String(deviceInfo.model || data.model || ""),
    androidVersion: String(deviceInfo.androidVersion || data.androidVersion || ""),
    assignedPlaylistId: data.assignedPlaylistId || null,
    assignedPlaylistName: String(data.assignedPlaylistName || ""),
    currentMediaId: data.currentMediaId || null,
    lastPublicationId: data.lastPublicationId || null,
    lastPublishedVersion: Math.max(0, Number(data.lastPublishedVersion || 0)),
    lastAppliedPublicationId: data.lastAppliedPublicationId || null,
    lastAppliedVersion: data.lastAppliedVersion == null ? null : Math.max(0, Number(data.lastAppliedVersion || 0)),
    lastSyncError: String(data.lastSyncError || ""),
    lastSyncErrorAt: data.lastSyncErrorAt || null,
    signageMode: String(data.signageMode || "STANDARD"),
    isDeviceOwner: data.isDeviceOwner === true,
    isLockTaskActive: data.isLockTaskActive === true,
    isManagedHome: data.isManagedHome === true,
    screenPolicy: String(data.screenPolicy || ""),
    playbackHealth: String(data.playbackHealth || "STARTING"),
    appVersion: String(data.appVersion || ""),
    lastSeenAt: data.lastSeenAt || null,
    registeredAt: data.registeredAt || null,
    pairedAt: data.pairedAt || null,
    disabledAt: data.disabledAt || null,
    revokedAt: data.revokedAt || null,
    updatedAt: data.updatedAt || null,
  };
  return {
    ...normalized,
    computedStatus: getComputedTvDeviceStatus(normalized),
    searchText: [
      normalized.deviceId,
      normalized.deviceName,
      normalized.city,
      normalized.state,
      normalized.country,
      normalized.manufacturer,
      normalized.model,
    ].join(" ").toLowerCase(),
  };
}

function normalizeTvPairing(snapshot) {
  const data = snapshot.data() || {};
  const requestedDeviceInfo = data.requestedDeviceInfo || {};
  const expiresAtMs = toMillis(data.expiresAt);
  const isExpired = expiresAtMs > 0 && Date.now() > expiresAtMs && data.status === TV_PAIRING_STATUSES.REQUESTED;
  return {
    id: snapshot.id,
    pairingId: String(data.pairingId || snapshot.id || ""),
    pairingCode: String(data.pairingCode || ""),
    status: isExpired ? TV_PAIRING_STATUSES.EXPIRED : String(data.status || TV_PAIRING_STATUSES.REQUESTED),
    deviceId: data.deviceId || null,
    deviceName: data.deviceName || null,
    installationId: String(data.installationId || ""),
    requestedDeviceInfo: {
      manufacturer: String(requestedDeviceInfo.manufacturer || ""),
      model: String(requestedDeviceInfo.model || ""),
      androidVersion: String(requestedDeviceInfo.androidVersion || ""),
      appVersion: String(requestedDeviceInfo.appVersion || ""),
    },
    approvedBy: data.approvedBy || null,
    createdAt: data.createdAt || null,
    expiresAt: data.expiresAt || null,
    approvedAt: data.approvedAt || null,
    completedAt: data.completedAt || null,
    rejectedAt: data.rejectedAt || null,
    createdAtMs: toMillis(data.createdAt),
    expiresAtMs,
    isExpired,
    searchText: `${data.pairingCode || ""} ${requestedDeviceInfo.manufacturer || ""} ${requestedDeviceInfo.model || ""}`.toLowerCase(),
  };
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const stamp = new Date(value).getTime();
  return Number.isFinite(stamp) ? stamp : 0;
}
